<template>
  <FormField name="address-lookup">
    <FormItem>
      <FormLabel>{{ props.label ? props.label : 'Address Lookup' }}</FormLabel>
      <FormControl>
        <div class="relative">
          <Input
            v-model="addressLookup"
            :disabled="props.disabled"
            type="text"
            placeholder="Start typing to search for you address"
          />
          <div
            v-if="addressSuggestions.length > 0"
            class="absolute left-0 right-0 top-12 bg-white shadow-xl border-gray-200 border rounded-lg p-2 z-10"
          >
            <div
              v-for="suggestion in addressSuggestions"
              :key="suggestion.placeId"
              class="flex flex-col gap-2 p-2 hover:bg-gray-100 rounded-lg cursor-default"
              @click="fetchPlaceDetails(suggestion.placeId, suggestion.text)"
            >
              {{ suggestion.text }}
            </div>
          </div>
        </div>
      </FormControl>
      <div class="flex justify-end mt-6">
        <img src="@/assets/google.png" alt="Google Logo" class="h-6" />
      </div>
    </FormItem>
  </FormField>
</template>

<script setup lang="ts">
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { debounce } from '@/utils/lowDash';
import { importLibrary } from '@googlemaps/js-api-loader';
import { onMounted, ref, watch } from 'vue';

const emits = defineEmits(['update:places-found']);
const props = defineProps<{
  address: string;
  label?: string;
  disabled?: boolean;
}>();

const addressLookup = ref<string>('');
const isTyping = ref<boolean>(false);
const skipNextAddressLookupWatch = ref(false);

// Autocomplete service for address suggestions and placeService for full place details
let autocompleteService: google.maps.places.AutocompleteSuggestion | null = null;

// Stores list of suggestions we get back from google
const addressSuggestions = ref<{ placeId: string; text: string }[]>([]);

onMounted(() => {
  const initAutoComplete = async () => {
    if (!autocompleteService) {
      await importLibrary('places');

      autocompleteService = new google.maps.places.AutocompleteSuggestion();
    }
  };

  initAutoComplete();
});

// Watch the input value and trigger the address suggestions function but not when a user clicks on a suggestion
watch(addressLookup, (newValue) => {
  if (skipNextAddressLookupWatch.value) {
    skipNextAddressLookupWatch.value = false;
    return;
  }

  if (isTyping.value) {
    fetchAddressSuggestions(newValue);
  }

  isTyping.value = true;
});

// Watch the address prop and update the input value
watch(
  () => props.address,
  (newValue) => {
    skipNextAddressLookupWatch.value = true;
    addressLookup.value = newValue;
  },
  { immediate: true },
);

// Fetch address suggestions based on the input value
const fetchAddressSuggestions = debounce(async (input: string) => {
  if (input === '') {
    addressSuggestions.value = [];
    return;
  }

  if (autocompleteService && input) {
    let sessionToken = new google.maps.places.AutocompleteSessionToken();
    let request = {
      input,
      sessionToken,
    };

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    addressSuggestions.value = [];

    for (let suggestion of suggestions) {
      if (suggestion.placePrediction) {
        addressSuggestions.value.push({
          placeId: suggestion.placePrediction.placeId,
          text: suggestion.placePrediction.text.text,
        });
      }
    }
  }
}, 300);

// Function to fetch full place details and fill form fields
const fetchPlaceDetails = async (placeId: string, description: string) => {
  addressSuggestions.value = [];
  isTyping.value = false;
  addressLookup.value = description;

  const place = new google.maps.places.Place({
    id: placeId,
    requestedLanguage: 'en',
  });

  const result = await place.fetchFields({ fields: ['addressComponents'] });

  if (result.place.addressComponents) {
    onPlaceFound(result.place.addressComponents);
  }
};

const onPlaceFound = (place: google.maps.places.AddressComponent[]) => {
  emits('update:places-found', place);
};
</script>
